import React, { SyntheticEvent } from 'react';
import styled from 'styled-components';

import 'video-react/dist/video-react.css';
import { Tag, Tags } from '../../../interfaces';
import { humanizeEnum } from '../../../utils';
import { Button, ButtonProps, ButtonsBox, CopyToClipboard, H3, SpaceBetween, Tooltip } from '../../atoms';
import { getTagIconComponent } from '../../../utils/tagIconsUtils';

interface Props {
  question: Tag;
  tags: any;
  setTags?: any;
  onUserTagChange?: any;
  disabled?: boolean;
  onTagClick: (
    path: Tag,
    value?: any,
    tags?: Tags,
    setTags?: (tags: Tags) => void,
    onUserTagChange?: (tags: Tags) => void,
  ) => (evt?: SyntheticEvent) => void;
  withCopy?: boolean;
  isAssetManagement?: boolean; // NEW: Flag to enable icon-based rendering
}

const getVariant = (question: Tag, value: any, tags: any, isAssetManagement?: boolean): ButtonProps['variant'] => {
  let variant = 'tag';
  const path = `td:${question.id}`;

  if (isAssetManagement) {
    variant = tags[path] === `ev:${value}` ? 'tagWithIconPrimary' : 'tagWithIconSecondary';
    return variant as ButtonProps['variant'];
  }

  if (question.value_type === 'bool') {
    if (tags[path] === true) {
      variant = 'accept';
    } else if (tags[path] === false) {
      variant = 'danger';
    }
  } else {
    variant = tags[path] === `ev:${value}` ? 'primary' : 'tag';
  }

  return variant as ButtonProps['variant'];
};

const CopyToClipboardWrapper = styled('div')`
  margin-left: 8px;
`;

const StyledIconWrapper = styled.span`
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  vertical-align: middle;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export function Question(props: Props) {
  const { question, tags, onTagClick, withCopy, setTags, onUserTagChange, disabled, isAssetManagement } = props;

  if (question.value_type === 'int') {
    // FIXME Zhush it up a little
    return (
      <SpaceBetween>
        <input
          type="range"
          min={question.min_value}
          max={question.max_value}
          key={question.id}
          value={tags[question.id]}
          onChange={onTagClick(question)}
        />
        <H3>{tags[question.id]}</H3>
      </SpaceBetween>
    );
  }

  if (question.value_type === 'bool') {
    // FIXME Zhush it up a little
    const variant = getVariant(question, question.id, tags);
    return (
      <ButtonsBox>
        <Button
          key={question.id + variant}
          variant={variant}
          data-test={variant === 'tag' ? 'button-tag-empty' : 'button-tag-filled'}
          onClick={onTagClick(question, question.id, setTags, onUserTagChange)}
          title={question.description}
          disabled={disabled}
        >
          {humanizeEnum(question.name)}
        </Button>
      </ButtonsBox>
    );
  }

  if (isAssetManagement) {
    return (
      <div>
        {question.enum_variants?.map((value) => {
          const variant = getVariant(question, value.id, tags, isAssetManagement);
          if (value.state !== 'active') {
            return null;
          }

          // Get icon component for asset management fleet
          const enumValue = `ev:${value.id}`;
          const isActive = variant === 'tagWithIconPrimary';
          const IconComponent = getTagIconComponent(enumValue, isActive);

          return (
            <Button
              key={value.id + variant}
              variant={variant}
              data-test={variant === 'tag' || variant === 'tagWithIconSecondary' ? 'button-tag-empty' : 'button-tag-filled'}
              onClick={onTagClick(question, value.id, tags, setTags, onUserTagChange)}
              title={value.description || humanizeEnum(value.variant_name)}
              disabled={disabled}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {IconComponent && (
                <StyledIconWrapper>
                  <IconComponent aria-label={humanizeEnum(value.variant_name)} />
                </StyledIconWrapper>
              )}
              {humanizeEnum(value.variant_name)}
              {withCopy && (
                <CopyToClipboardWrapper>
                  <Tooltip tooltip={`Enum ID: ${value.id}`}>
                    <CopyToClipboard textToCopy={`${value.id}`} />
                  </Tooltip>
                </CopyToClipboardWrapper>
              )}
            </Button>
          );
        })}
      </div>
    );
  }

  return (
    <ButtonsBox>
      {question.enum_variants?.map((value) => {
        const variant = getVariant(question, value.id, tags);
        if (value.state !== 'active') {
          return null;
        }

        return (
          <Button
            key={value.id + variant}
            variant={variant}
            data-test={variant === 'tag' ? 'button-tag-empty' : 'button-tag-filled'}
            onClick={onTagClick(question, value.id, tags, setTags, onUserTagChange)}
            title={value.description || humanizeEnum(value.variant_name)}
            disabled={disabled}
          >
            {humanizeEnum(value.variant_name)}
            {withCopy && (
              <CopyToClipboardWrapper>
                <Tooltip tooltip={`Enum ID: ${value.id}`}>
                  <CopyToClipboard textToCopy={`${value.id}`} />
                </Tooltip>
              </CopyToClipboardWrapper>
            )}
          </Button>
        );
      })}
    </ButtonsBox>
  );
}
